/**
 * Difficulty Tier Mapping Utility
 *
 * ============================================================
 * 🔄 MIGRATION STATUS: ACTIVE
 * ============================================================
 *
 * This utility provides the mapping between the existing difficulty ID system
 * (101-306) and the new continuous difficultyTier system (1-18).
 *
 * PURPOSE:
 * During the migration period, this utility allows the adaptive engine and
 * other systems to work with difficultyTier values even though the JSON
 * question files have not yet been updated with explicit difficultyTier fields.
 *
 * The mapping derives difficultyTier, levelCode, and group from the existing
 * difficulty ID. When JSONs are eventually migrated to include explicit
 * difficultyTier values, those will take precedence over derived values.
 *
 * See: src/docs/FAMILY_ID_MIGRATION.md for full specification
 * ============================================================
 */

import { DifficultyLevel } from '../types'

/**
 * DifficultyTier Scale (continuous 1-18):
 *
 * Group 6:
 *   B6  = tier 1   (difficulty ID: 101)
 *   B6+ = tier 2   (difficulty ID: 102)
 *   M6  = tier 3   (difficulty ID: 103)
 *   M6+ = tier 4   (difficulty ID: 104)
 *   E6  = tier 5   (difficulty ID: 105)
 *   E6+ = tier 6   (difficulty ID: 106)
 *
 * Group 7:
 *   B7  = tier 7   (difficulty ID: 201)
 *   B7+ = tier 8   (difficulty ID: 202)
 *   M7  = tier 9   (difficulty ID: 203)
 *   M7+ = tier 10  (difficulty ID: 204)
 *   E7  = tier 11  (difficulty ID: 205)
 *   E7+ = tier 12  (difficulty ID: 206)
 *
 * Group 8:
 *   B8  = tier 13  (difficulty ID: 301)
 *   B8+ = tier 14  (difficulty ID: 302)
 *   M8  = tier 15  (difficulty ID: 303)
 *   M8+ = tier 16  (difficulty ID: 304)
 *   E8  = tier 17  (difficulty ID: 305)
 *   E8+ = tier 18  (difficulty ID: 306)
 */

/** Bidirectional mapping: difficulty ID ↔ tier number */
const DIFFICULTY_TO_TIER: Record<number, number> = {
  101: 1,
  102: 2,
  103: 3,
  104: 4,
  105: 5,
  106: 6, // Group 6
  201: 7,
  202: 8,
  203: 9,
  204: 10,
  205: 11,
  206: 12, // Group 7
  301: 13,
  302: 14,
  303: 15,
  304: 16,
  305: 17,
  306: 18, // Group 8
}

const TIER_TO_DIFFICULTY: Record<number, number> = Object.fromEntries(
  Object.entries(DIFFICULTY_TO_TIER).map(([k, v]) => [v, Number(k)]),
)

/** Mapping: difficulty ID → levelCode string */
const DIFFICULTY_TO_LEVEL_CODE: Record<number, string> = {
  101: 'B6',
  102: 'B6+',
  103: 'M6',
  104: 'M6+',
  105: 'E6',
  106: 'E6+',
  201: 'B7',
  202: 'B7+',
  203: 'M7',
  204: 'M7+',
  205: 'E7',
  206: 'E7+',
  301: 'B8',
  302: 'B8+',
  303: 'M8',
  304: 'M8+',
  305: 'E8',
  306: 'E8+',
}

/** Mapping: difficulty ID → group number */
const DIFFICULTY_TO_GROUP: Record<number, number> = {
  101: 6,
  102: 6,
  103: 6,
  104: 6,
  105: 6,
  106: 6,
  201: 7,
  202: 7,
  203: 7,
  204: 7,
  205: 7,
  206: 7,
  301: 8,
  302: 8,
  303: 8,
  304: 8,
  305: 8,
  306: 8,
}

/**
 * Derive difficultyTier from a difficulty ID.
 * Returns undefined if the difficulty ID is not recognized.
 */
export function getDifficultyTier(
  difficultyId: DifficultyLevel,
): number | undefined {
  return DIFFICULTY_TO_TIER[difficultyId]
}

/**
 * Derive levelCode from a difficulty ID.
 * Returns undefined if the difficulty ID is not recognized.
 */
export function getLevelCode(
  difficultyId: DifficultyLevel,
): string | undefined {
  return DIFFICULTY_TO_LEVEL_CODE[difficultyId]
}

/**
 * Derive group number from a difficulty ID.
 * Returns undefined if the difficulty ID is not recognized.
 */
export function getGroupFromDifficulty(
  difficultyId: DifficultyLevel,
): number | undefined {
  return DIFFICULTY_TO_GROUP[difficultyId]
}

/**
 * Convert a tier number back to a difficulty ID.
 * Returns undefined if the tier is not recognized.
 */
export function getDifficultyFromTier(
  tier: number,
): DifficultyLevel | undefined {
  return TIER_TO_DIFFICULTY[tier]
}

/**
 * Derive all new schema fields from an existing difficulty ID.
 *
 * This is the primary function used during the migration period.
 * It computes difficultyTier, levelCode, and group from the difficulty ID,
 * allowing the system to work with the new schema before JSONs are updated.
 *
 * @param difficultyId - The existing difficulty ID (101-306)
 * @returns Object with derived fields, or empty object if ID is unrecognized
 */
export function deriveMigrationFields(difficultyId: DifficultyLevel): {
  difficultyTier?: number
  levelCode?: string
  group?: number
} {
  const tier = DIFFICULTY_TO_TIER[difficultyId]
  if (tier === undefined) return {}

  return {
    difficultyTier: tier,
    levelCode: DIFFICULTY_TO_LEVEL_CODE[difficultyId],
    group: DIFFICULTY_TO_GROUP[difficultyId],
  }
}

/**
 * Validate that a difficultyTier value is consistent with a difficulty ID.
 * Used during migration to catch mismatches between explicit and derived values.
 *
 * @returns true if consistent (or if either value is missing), false if mismatch
 */
export function validateTierConsistency(
  difficultyId: DifficultyLevel,
  explicitTier?: number,
): boolean {
  if (explicitTier === undefined) return true
  const derivedTier = DIFFICULTY_TO_TIER[difficultyId]
  if (derivedTier === undefined) return true
  return explicitTier === derivedTier
}

/**
 * Get all valid tier numbers (1-18).
 */
export function getAllTiers(): number[] {
  return Array.from({ length: 18 }, (_, i) => i + 1)
}

/**
 * Get tier range for a specific group.
 */
export function getTierRangeForGroup(
  group: number,
): { min: number; max: number } | undefined {
  switch (group) {
    case 6:
      return { min: 1, max: 6 }
    case 7:
      return { min: 7, max: 12 }
    case 8:
      return { min: 13, max: 18 }
    default:
      return undefined
  }
}
